// src/components/customers/JobModal.jsx

/**
 * JobModal Component
 * Comprehensive modal for adding/editing jobs with all fields including address
 * Features enhanced UI/UX and mobile responsiveness without external dependencies
 */
const JobModal = ({ isOpen, onClose, params, onInputChange, onSave }) => {
  if (!isOpen) return null;

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal container - responsive and scrollable */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 sticky top-0 z-10">
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-bold">
                {params.id ? "Edit Job" : "Add New Job"}
              </h3>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors text-2xl leading-none"
              >
                ×
              </button>
            </div>
          </div>

          {/* Modal Body - Scrollable */}
          <form
            onSubmit={handleSubmit}
            className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]"
          >
            <div className="space-y-6">
              {/* Date Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="date"
                  value={
                    params.date
                      ? params.date.split("/").reverse().join("-")
                      : ""
                  }
                  onChange={(e) => {
                    const date = e.target.value.split("-").reverse().join("/");
                    onInputChange({ target: { id: "date", value: date } });
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 
                    focus:ring-blue-500 focus:border-transparent transition-all"
                  required
                />
              </div>

              {/* Plan and Products in a grid on larger screens */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Plan Field */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Plan
                  </label>
                  <textarea
                    id="plan"
                    value={params.plan || ""}
                    onChange={onInputChange}
                    placeholder="Enter job plan..."
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none
                      focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* Products Field */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Products
                  </label>
                  <textarea
                    id="products"
                    value={params.products || ""}
                    onChange={onInputChange}
                    placeholder="Enter products needed..."
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none
                      focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              {/* Address Section */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <span className="text-blue-500">📍</span>
                  Job Location
                </h4>

                <div className="grid md:grid-cols-2 gap-4">
                  {/* Address Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={params.address || ""}
                      onChange={onInputChange}
                      placeholder="Enter job address..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg
                        focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      required
                    />
                  </div>

                  {/* Postcode Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Postcode <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg">
                        #
                      </span>
                      <input
                        type="text"
                        id="postcode"
                        name="postcode"
                        value={params.postcode || ""}
                        onChange={onInputChange}
                        placeholder="Enter postcode..."
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg
                          focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Checkbox Section */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Job Requirements
                </h4>

                <div className="grid sm:grid-cols-2 gap-3">
                  {/* Checkboxes */}
                  {[
                    { id: "hasUnderlay", label: "Has Underlay" },
                    { id: "hasGrippers", label: "Has Grippers" },
                    { id: "floorIsGood", label: "Floor is Good" },
                    { id: "oldFlooringRemoved", label: "Old Flooring Removed" },
                    { id: "furnitureRemoval", label: "Furniture Removal" },
                    { id: "concrete", label: "Concrete" },
                  ].map((item) => (
                    <label
                      key={item.id}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg 
                        hover:bg-gray-100 transition-colors cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        id={item.id}
                        checked={params[item.id] || false}
                        onChange={onInputChange}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        {item.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Number Inputs */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Doors Need Cutting
                  </label>
                  <input
                    type="number"
                    id="doorsNeedCutting"
                    value={params.doorsNeedCutting || 0}
                    onChange={onInputChange}
                    min="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg
                      focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Number of Door Plates Needed
                  </label>
                  <input
                    type="number"
                    id="numberOfDoorPlateNeeded"
                    value={params.numberOfDoorPlateNeeded || 0}
                    onChange={onInputChange}
                    min="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg
                      focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              {/* Comments Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Comments
                </label>
                <textarea
                  id="comments"
                  value={params.comments || ""}
                  onChange={onInputChange}
                  placeholder="Additional comments..."
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none
                    focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            </div>
          </form>

          {/* Modal Footer - Sticky */}
          <div className="bg-gray-50 px-6 py-4 sticky bottom-0 border-t">
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium
                  hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 
                  focus:ring-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 
                  text-white rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 
                  transition-all transform hover:scale-[1.02] focus:outline-none 
                  focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {params.id ? "Update Job" : "Save Job"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobModal;
